// Pure data — no logic, no React imports.
// Extracted from ShortcutNavBar to keep component files within the ~200-line limit.

import { getModuleRegistryTree, isSubGroup } from '../constants/modules';

/**
 * Walks the full MODULE_REGISTRY tree at module-load time and produces a map of
 * route path → i18n labelKey for every navigable entry (those with both path and
 * labelKey defined). This covers all registry-driven sidebar links automatically so
 * shortcutPathToTranslationKey only needs manual overrides for non-registry paths.
 *
 * Circular-import check: shortcutNavBarMaps is imported by ShortcutNavBar (component),
 * NOT by modules.ts, so there is no cycle.
 */
function buildRegistryPathMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const group of getModuleRegistryTree()) {
    for (const child of group.children) {
      if (isSubGroup(child)) {
        for (const entry of child.children) {
          if (entry.path && entry.labelKey) {
            map[entry.path] = entry.labelKey;
          }
        }
      } else {
        if (child.path && child.labelKey) {
          map[child.path] = child.labelKey;
        }
      }
    }
  }
  return map;
}

/**
 * Registry-driven path → i18n key.
 * Built once at module load; covers every ModuleEntry that has a path.
 * Resolution priority: shortcutPathToTranslationKey (manual overrides) → this map
 * → entityNameToTranslationKey → raw stored name.
 */
export const registryPathToTranslationKey: Record<string, string> = buildRegistryPathMap();

/** Maps a stored entity name (any language) → i18n translation key. */
export const entityNameToTranslationKey: Record<string, string> = {
  // Partners / Партнери
  'Партнери': 'sidebar.partner',
  'Partners': 'sidebar.partner',
  'Partnerët': 'sidebar.partner',
  // Countries / Држави
  'Држави': 'sidebar.countries',
  'Countries': 'sidebar.countries',
  'Vendet': 'sidebar.countries',
  // Currency / Валута
  'Валута': 'tableHeaders.currency',
  'Currency': 'tableHeaders.currency',
  'Monedha': 'tableHeaders.currency',
  'Valuta': 'tableHeaders.currency',
  // Banks / Банки
  'Банки': 'sidebar.banks',
  'Banks': 'sidebar.banks',
  'Bankat': 'sidebar.banks',
  // Municipalities / Општина
  'Општина': 'sidebar.municipalities',
  'Општини': 'sidebar.municipalities',
  'Municipalities': 'sidebar.municipalities',
  'Komunat': 'sidebar.municipalities',
  // Unit Measurement / Единица Мерка
  'Единица Мерка': 'tableHeaders.unitMeasurment',
  'Unit Measurment': 'tableHeaders.unitMeasurment',
  'Njësia e Matjes': 'tableHeaders.unitMeasurment',
  // Classification / Класификација
  'Класификација': 'tableHeaders.classification',
  'Classification': 'tableHeaders.classification',
  'Klasifikimi': 'tableHeaders.classification',
  // Serial Numbers / Шифрарник на Сериски бр
  'Шифрарник на Сериски бр': 'tableHeaders.serialNumbers',
  'Serial Numbers': 'tableHeaders.serialNumbers',
  'Numrat Serial': 'tableHeaders.serialNumbers',
  // Vat Types / Тип на ДДВ
  'Тип на ДДВ': 'sidebar.vatTypes',
  'Vat Types': 'sidebar.vatTypes',
  'Llojet e TVSH-së': 'sidebar.vatTypes',
  // Parities / Паритети
  'Паритети': 'sidebar.parities',
  'Parities': 'sidebar.parities',
  'Paritetet': 'sidebar.parities',
  // Setting Selling Prices / Подесување на продажни цени
  'Подесување на продажни цени': 'sidebar.settingPurchasePrices',
  'Setting selling prices': 'sidebar.settingPurchasePrices',
  // Accounting Type / Книговодствени видови
  'Книговодствени видови': 'headers.accountingType',
  'Accounting Type': 'headers.accountingType',
  // HR - Basic Data / Кадри - Основни податоци
  'Кадри - Основни податоци': 'hr.basicData.title',
  'HR - Basic Data': 'hr.basicData.title',
  // Stock Overview / Преглед на залихи
  'Преглед на залихи': 'stockOverview.title',
  'Stock Overview': 'stockOverview.title',
  // Stock Management / Управување со залихи
  'Управување со залихи': 'sidebar.warehouses',
  'Stock Management': 'sidebar.warehouses',
  // Warehouses / Магацини
  'Магацини': 'sidebar.warehouses',
  'Warehouses': 'sidebar.warehouses',
  // Warehouse Card / Магацински картон
  'Магацински картон': 'warehouseCard.title',
  'Warehouse Card': 'warehouseCard.title',
  // Documents / Шифрарник на Вид Док
  'Шифрарник на Вид Док': 'sidebar.documents',
  'Documents': 'sidebar.documents',
  'Dokumentet': 'sidebar.documents',
  // Copy Ingredients / Копирање Составници
  'Копирање Составници': 'sidebar.copyIngredients',
  'Copy Ingredients': 'sidebar.copyIngredients',
  // Use of Components / Употреба на норматив
  'Употреба на норматив': 'sidebar.useOfComponents',
  'Употреба на Составници': 'sidebar.useOfComponents',
  'Use of Components': 'sidebar.useOfComponents',
  // Items Control Panel / Контролна Табла Иденти
  'Items Control Panel': 'items.controlPanel',
  'Контролна Табла Иденти': 'items.controlPanel',
  // Backwards compatibility for saved shortcuts
  'Items Print Control Panel': 'items.controlPanel',
  'Procurement Control Panel': 'procurement.controlPanel',
  'Procurement Print Control Panel': 'procurement.controlPanel',
  'Контролна Табла Набавка': 'procurement.controlPanel',
  'Контролен панел за испис на набавка': 'procurement.controlPanel',
  'Контролна Табла за Печатење Идент': 'items.controlPanel',
  // Items / Идент (all possible variations)
  'Идент': 'sidebar.items',
  'Иденти': 'sidebar.items',
  'Item': 'sidebar.items',
  'Items': 'sidebar.items',
  'Ident': 'sidebar.items',
  'Idents': 'sidebar.items',
  'Artikulli': 'sidebar.items',
  'Artikujt': 'sidebar.items',
  'Артикл': 'sidebar.items',
  'Артикли': 'sidebar.items',
  'артикл': 'sidebar.items',
  'артикли': 'sidebar.items',
  'идент': 'sidebar.items',
  'иденти': 'sidebar.items',
  'item': 'sidebar.items',
  'items': 'sidebar.items',
  // Printers / Печатачи
  'Печатачи': 'sidebar.printers',
  'Printers': 'sidebar.printers',
  // Phones / Телефони
  'Телефони': 'sidebar.phones',
  'Phones': 'sidebar.phones',
  // Computers / Компјутери
  'Компјутери': 'sidebar.computers',
  'Computers': 'sidebar.computers',
  // Devices / Уреди
  'Уреди': 'sidebar.devices',
  'Devices': 'sidebar.devices',
  // Reserved Contacts / Резервирани Контакти
  'Резервирани Контакти': 'sidebar.reservedContacts',
  'Reserved Contacts': 'sidebar.reservedContacts',
};

/**
 * Maps a known route path → i18n translation key.
 * Takes precedence over registryPathToTranslationKey and entityNameToTranslationKey.
 * Use this for:
 *   (a) paths that ARE in the registry but need a label override (e.g. /stock-management
 *       shows "Warehouses" in the sidebar, not the group label "Stock Management")
 *   (b) classification sub-type routes (/classification/*) that are NOT in the registry
 *   (c) control-panel routes that are hardcoded in sidebar components but NOT in registry
 *   (d) other hardcoded sidebar paths not reachable from the registry
 */
export const shortcutPathToTranslationKey: Record<string, string> = {
  // ── Registry entries with sidebar-label overrides ────────────────────────
  // The registry stores /stock-management under labelKey: 'sidebar.stockManagement'
  // (the group label) but the sidebar link uses 'sidebar.warehouses'. Keep this override.
  '/partners': 'sidebar.partner',
  '/dashboard': 'sidebar.communication',
  '/banks': 'sidebar.banks',
  '/countries': 'sidebar.countries',
  '/currencies': 'tableHeaders.currency',
  '/stock-management': 'sidebar.warehouses',
  '/users': 'sidebar.users',
  '/administration-console/users': 'sidebar.users',
  '/municipalities': 'sidebar.municipalities',
  '/hr/basic-data': 'hr.basicData.title',
  '/items': 'sidebar.items',
  '/accounting-type': 'headers.accountingType',
  '/unit-measurment': 'tableHeaders.unitMeasurment',
  '/serial-numbers': 'tableHeaders.serialNumbers',
  '/vat': 'sidebar.vatTypes',
  '/packing-materials': 'sidebar.packagingMaterials',
  '/os': 'sidebar.osBasicSpecification',
  '/company-settings': 'companySettings.title',
  '/administration-console/database/add': 'sidebar.databaseAdd',
  '/documents-config': 'sidebar.documents',
  '/documentation-help': 'documentationHelp.title',
  '/help': 'sidebarFooter.help',
  '/payment-methods': 'sidebar.paymentMethod',
  '/certificate-preview': 'sidebar.certificates',
  '/print-form-designer': 'sidebar.printForms',
  '/print-preview': 'printPreview.printPreview',
  '/generic-print-preview': 'printPreview.printPreview',
  '/items-print-preview': 'printPreview.printPreview',
  '/ingridiens-print-preview': 'printPreview.printPreview',
  '/partners-print-preview': 'printPreview.printPreview',
  '/contracted-prices-print-preview': 'printPreview.printPreview',
  '/computer-system-print-preview': 'printPreview.printPreview',
  '/inventory-count-print-preview': 'printPreview.printPreview',
  '/inventory-adjustment-print-preview': 'printPreview.printPreview',
  '/warehouse-card-control-panel': 'sidebar.printControlPanel',
  '/warehouse-card-print-preview': 'printPreview.printPreview',
  '/procurement-print-preview': 'printPreview.printPreview',
  '/sales-print-preview': 'printPreview.printPreview',
  '/production-print-preview': 'printPreview.printPreview',
  '/qual-doc-print-preview': 'qualDoc.printPreview',
  '/hr/departments': 'sidebar.departments',
  '/computer-system/devices': 'sidebar.devices',

  // ── Classification sub-type routes (NOT in registry — hardcoded in SidebarMasterData) ──
  '/classification/production-department': 'sidebar.classificationTypes.productionDepartment',
  '/classification/collection': 'sidebar.classificationTypes.collection',
  '/classification/classification': 'sidebar.classificationTypes.classification',
  '/classification/raw-material-type': 'sidebar.classificationTypes.rawMaterialType',
  '/classification/pa-type': 'sidebar.classificationTypes.paType',
  '/classification/sa-type': 'sidebar.classificationTypes.saType',
  '/classification/partner-type': 'sidebar.classificationTypes.partnerType',

  // ── Control panel routes (hardcoded in sidebar components, NOT in registry) ─────────
  // All use the same sidebar.printControlPanel label that the sidebar link uses.
  '/partners-control-panel': 'sidebar.printControlPanel',
  '/contracted-prices-control-panel': 'contractedPrices.controlPanel',
  '/banks-control-panel': 'sidebar.printControlPanel',
  '/currencies-control-panel': 'sidebar.printControlPanel',
  '/countries-control-panel': 'sidebar.printControlPanel',
  '/municipalities-control-panel': 'sidebar.printControlPanel',
  '/parities-control-panel': 'sidebar.printControlPanel',
  '/items-control-panel': 'sidebar.printControlPanel',
  '/ingridiens-control-panel': 'sidebar.printControlPanel',
  '/accounting-types-control-panel': 'sidebar.printControlPanel',
  '/unit-measurements-control-panel': 'sidebar.printControlPanel',
  '/serial-numbers-control-panel': 'sidebar.printControlPanel',
  '/packaging-materials-control-panel': 'sidebar.printControlPanel',
  '/vat-types-control-panel': 'sidebar.printControlPanel',
  '/classification-control-panel': 'sidebar.printControlPanel',
  '/procurement-control-panel': 'sidebar.printControlPanel',
  '/sales-control-panel': 'sidebar.printControlPanel',
  '/production-control-panel': 'production.controlPanel',
  '/stock-control-panel': 'sidebar.printControlPanel',
  '/computer-system-control-panel': 'sidebar.printControlPanel',
  // Quality-docs control panel uses a distinct label
  '/qual-doc-control-panel': 'sidebar.docControlPanel',

  // ── HR sub-routes (hardcoded in SidebarHR, NOT in registry) ─────────────────────────
  '/hr/workplaces': 'workplaces.title',
  '/hr/contract-types': 'sidebar.contractTypes',
  '/hr/education-levels': 'sidebar.educationLevels',
  '/hr/salary-grades': 'sidebar.salaryGrades',
  '/hr/holidays': 'sidebar.holidays',
  '/hr/absence-types': 'sidebar.absenceTypes',
  '/hr/shifts': 'sidebar.shifts',
  '/hr/contributions': 'sidebar.contributions',
  '/hr/payroll': 'sidebar.payroll',

  // ── Stock management sub-routes (hardcoded in SidebarStockManagement, NOT in registry) ──
  '/stock-management/warehouse-card': 'sidebar.documentMovements',
  '/stock-management/stock-overview': 'sidebar.stockOverview',
  '/storehouse/search-board': 'sidebar.storehouseSearchBoard',

  // ── Computer system sub-routes (hardcoded in SidebarComputerSystem, NOT in registry) ──
  '/computer-system/computer-stock': 'computerStock.title',
  '/computer-system/credentials': 'credentials.title',
  '/computer-system/it-procedures': 'itProcedures.title',

  // ── Finance sub-routes (hardcoded in SidebarFinancial, NOT in registry) ─────────────
  '/finance/general-ledger/chart-of-accounts': 'sidebar.chartOfAccounts',
  '/finance/general-ledger/journal-entries': 'sidebar.journalEntries',
  '/finance/general-ledger/trial-balance': 'sidebar.trialBalance',
  '/finance/general-ledger/analytical-cards': 'sidebar.analyticalCards',
  '/finance/vat/input-register': 'sidebar.inputInvoiceRegister',
  '/finance/vat/output-register': 'sidebar.outputInvoiceRegister',
  '/finance/vat/ddv-04': 'sidebar.ddv04',
  '/finance/payments/payment-orders': 'sidebar.paymentOrders',
  '/finance/payments/offsetting': 'sidebar.offsetting',
  '/finance/payments/cash-register': 'sidebar.cashRegister',
  '/finance/bank/statements': 'sidebar.bankStatements',
  '/finance/bank/reconciliation': 'sidebar.bankReconciliation',
  '/finance/receivables/customers': 'sidebar.customerBalances',
  '/finance/receivables/suppliers': 'sidebar.supplierBalances',
  '/finance/receivables/ios': 'sidebar.openItemsStatement',
  '/finance/receivables/aging': 'sidebar.agingAnalysis',
  '/finance/reports/trial-balance': 'sidebar.trialBalance',
  '/finance/reports/balance-sheet': 'sidebar.balanceSheet',
  '/finance/reports/income-statement': 'sidebar.incomeStatement',
  '/finance/reports/cash-flow': 'sidebar.cashFlow',
  '/finance/ujp/ddv-04': 'sidebar.ddv04',
  '/finance/ujp/annual-account': 'sidebar.annualAccount',
  '/finance/ujp/tax-balance': 'sidebar.taxBalance',
};
